<template>
  <div class="network-tree-node">
    <div
      class="network-tree-node__card"
      :class="{
        'network-tree-node__card--premium': node.isPremiumActive,
        'network-tree-node__card--non-premium': !node.isPremiumActive,
      }"
    >
      <div class="network-tree-node__header">
        <v-btn v-if="hasChildren" icon x-small @click="expanded = !expanded">
          <v-icon small>{{ expanded ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
        </v-btn>
        <span v-else class="network-tree-node__spacer" />
        <v-chip x-small outlined class="level-chip mr-2">N{{ node.level }}</v-chip>
        <span class="network-tree-node__name">{{ node.name }}</span>
        <v-chip
          x-small
          :color="node.isPremiumActive ? 'green darken-2' : 'grey'"
          dark
          class="ml-2"
        >
          {{ node.isPremiumActive ? "Premium" : "Não Premium" }}
        </v-chip>
        <v-chip
          x-small
          :color="statusColor(node.status)"
          dark
          class="ml-1"
        >
          {{ statusLabel(node.status) }}
        </v-chip>
        <v-chip
          v-if="!node.pagbankConnected"
          x-small
          color="amber lighten-4"
          text-color="amber darken-4"
          class="ml-1"
        >
          Sem PagBank
        </v-chip>
      </div>
      <div class="network-tree-node__meta">
        <span><v-icon x-small>mdi-card-account-details</v-icon> {{ formatDocument(node.document) }}</span>
        <span v-if="node.email"><v-icon x-small>mdi-email</v-icon> {{ node.email }}</span>
        <span v-if="node.phone"><v-icon x-small>mdi-phone</v-icon> {{ formatPhone(node.phone) }}</span>
        <span v-if="node.premiumJoinedAt">
          <v-icon x-small>mdi-star</v-icon> Premium desde {{ formatDate(node.premiumJoinedAt) }}
        </span>
        <span v-if="node.createdAt">
          <v-icon x-small>mdi-calendar</v-icon> Cadastro {{ formatDate(node.createdAt) }}
        </span>
      </div>
    </div>
    <div v-if="expanded && hasChildren" class="network-tree-node__children">
      <network-tree-node
        v-for="child in node.children"
        :key="child.id"
        :node="child"
        :depth="depth + 1"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { format } from "date-fns";
import Vue from "vue";
import { AssociateNetworkNode } from "@/types/franchise-associate/AssociateNetwork";

export default Vue.extend({
  name: "NetworkTreeNode",
  props: {
    node: {
      type: Object as () => AssociateNetworkNode,
      required: true,
    },
    depth: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      expanded: this.depth <= 2,
    };
  },
  computed: {
    hasChildren(): boolean {
      return (this.node.children?.length ?? 0) > 0;
    },
  },
  methods: {
    formatDate(value: string) {
      try {
        return format(new Date(value), "dd/MM/yyyy");
      } catch {
        return "—";
      }
    },
    formatDocument(value: string) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length === 11) {
        return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }
      if (digits.length === 14) {
        return digits.replace(
          /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
          "$1.$2.$3/$4-$5"
        );
      }
      return value || "—";
    },
    formatPhone(value: string) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length < 10) return value || "—";
      return `(${digits.slice(0, 2)}) ${digits.slice(2, digits.length === 11 ? 7 : 6)}-${digits.slice(digits.length === 11 ? 7 : 6)}`;
    },
    statusColor(status: string) {
      if (status === "INACTIVE") return "grey";
      if (status === "PENDING") return "amber darken-2";
      return "green darken-2";
    },
    statusLabel(status: string) {
      const map: Record<string, string> = {
        ACTIVE: "Ativo",
        PENDING: "Pendente",
        INACTIVE: "Inativo",
      };
      return map[status] || status;
    },
  },
});
</script>

<style scoped>
.network-tree-node__card {
  border-left: 4px solid #ccc;
  padding: 12px 14px;
  margin: 6px 0;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
}

.network-tree-node__card--premium {
  border-left-color: #2e7d32;
  background: linear-gradient(90deg, rgba(46, 125, 50, 0.04), #fff 40%);
}

.network-tree-node__card--non-premium {
  border-left-color: #bdbdbd;
}

.network-tree-node__header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-weight: 600;
}

.network-tree-node__name {
  font-size: 0.95rem;
}

.network-tree-node__spacer {
  width: 28px;
  display: inline-block;
}

.level-chip {
  font-weight: 700;
  min-width: 32px;
  justify-content: center;
}

.network-tree-node__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  font-size: 0.78rem;
  color: #555;
  margin-top: 8px;
  padding-left: 32px;
}

.network-tree-node__children {
  margin-left: 20px;
  border-left: 2px dashed #e0e0e0;
  padding-left: 10px;
}
</style>
